import React, { useState, useCallback, useEffect } from 'react';
import { ColumnNavigation } from './common/ColumnNavigation';
import {
  Box as MuiBox,
  Typography as MuiTypography,
  Paper as MuiPaper,
  Drawer as MuiDrawer,
  List as MuiList,
  ListItem as MuiListItem,
  ListItemButton as MuiListItemButton,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  Divider as MuiDivider,
  TextField as MuiTextField,
  Button as MuiButton,
  Switch as MuiSwitch,
  FormControlLabel as MuiFormControlLabel,
  Card as MuiCard,
  CardContent as MuiCardContent,
  CardHeader as MuiCardHeader,
  Grid as MuiGrid,
  Chip as MuiChip,
  IconButton as MuiIconButton,
  useMediaQuery,
  useTheme,
  Avatar as MuiAvatar,
  LinearProgress as MuiLinearProgress,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  Fade as MuiFade,
  Select as MuiSelect,
  MenuItem as MuiMenuItem,
  FormControl as MuiFormControl,
  InputLabel as MuiInputLabel,
  Container as MuiContainer,
  Slider as MuiSlider,
  ToggleButtonGroup as MuiToggleButtonGroup,
  ToggleButton as MuiToggleButton,
  Tooltip as MuiTooltip,
  InputAdornment as MuiInputAdornment,
  Alert as MuiAlert,
  Snackbar as MuiSnackbar
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as MailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Security as SecurityIcon,
  Notifications as BellIcon,
  CreditCard as CreditCardIcon,
  Shield as ShieldIcon,
  Language as GlobeIcon,
  Business as BuildingIcon,
  History as HistoryIcon,
  Delete as TrashIcon,
  Menu as MenuIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Star as StarIcon,
  DragIndicator as DragIcon,
  Palette as PaletteIcon,
  Download as DownloadIcon,
  LightMode as SunIcon,
  DarkMode as MoonIcon,
  Computer as MonitorIcon,
  Translate as TranslateIcon,
  Help as HelpIcon,
  Preview as PreviewIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  Group as GroupIcon,
  AccountBalance as AccountBalanceIcon,
  Policy as PolicyIcon,
  Logout as LogoutIcon,
  MenuBook as TerminologyIcon,
  Dashboard as DashboardIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudDone as CloudDoneIcon,
  Storage as StorageIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  ContentCopy as ContentCopyIcon,
  SmartToy as SmartToyIcon
} from '@mui/icons-material';

interface ProfileProps {
  currentView: string;
  onViewChange: (view: string) => void;
  onAIChatToggle: () => void;
}

const Profile: React.FC<ProfileProps> = ({ currentView, onViewChange, onAIChatToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState('profile-information');
  
  // Profile Information state
  const [profileData, setProfileData] = useState({
    firstName: 'John',
    lastName: 'Smith',
    email: 'john.smith@company.com',
    company: 'Intelligenic Inc.',
    jobTitle: 'Senior Product Manager',
    role: 'Product Manager',
    department: 'Product Management',
    industry: 'Software Technology',
    avatarUrl: ''
  });
  const [editMode, setEditMode] = useState(false);



  // Login & Security state
  const [passwordStrength, setPasswordStrength] = useState(75);
  const [mfaEnabled, setMfaEnabled] = useState(true);
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [securityEvents, setSecurityEvents] = useState([
    { date: '2024-01-20', event: 'Password changed', ip: '192.168.1.100' },
    { date: '2024-01-19', event: 'MFA enabled', ip: '192.168.1.100' },
    { date: '2024-01-15', event: 'Login from new device', ip: '192.168.1.200' }
  ]);

  // Notifications state
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    sms: false,
    weeklyDigest: true,
    projectUpdates: true,
    teamMentions: true,
    marketingEmails: false
  });

  // Subscription state
  const [currentPlan, setCurrentPlan] = useState('professional');
  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      price: '$9/month',
      features: ['5 Products', '10 Team Members', 'Basic Support', '1GB Storage'],
      current: false
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$29/month',
      features: ['25 Products', '50 Team Members', 'Priority Support', '10GB Storage', 'Advanced Analytics'],
      current: true,
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '$99/month',
      features: ['Unlimited Products', 'Unlimited Members', '24/7 Support', '100GB Storage', 'Custom Integrations', 'SSO'],
      current: false
    }
  ];

  // Billing state
  const [billingHistory, setBillingHistory] = useState([
    { id: 1, date: '2024-01-01', amount: '$29.00', status: 'Paid', invoice: 'INV-2024-001' },
    { id: 2, date: '2023-12-01', amount: '$29.00', status: 'Paid', invoice: 'INV-2023-012' },
    { id: 3, date: '2023-11-01', amount: '$29.00', status: 'Paid', invoice: 'INV-2023-011' }
  ]);

  // Privacy state
  const [privacySettings, setPrivacySettings] = useState({
    analytics: true,
    marketing: false,
    thirdPartySharing: false,
    profileVisibility: 'team',
    activityTracking: true
  });

  // Language & Region state
  const [localeSettings, setLocaleSettings] = useState({
    language: 'en-US',
    timezone: 'America/Los_Angeles',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12-hour'
  });

  // Team/Organization state
  const [teamSettings, setTeamSettings] = useState({
    organizationName: 'Intelligenic Inc.',
    teamRole: 'Product Manager',
    directReports: 3,
    department: 'Product',
    manager: 'Sarah Johnson'
  });

  // Theme state
  const [themeMode, setThemeMode] = useState('light');
  const [contrastLevel, setContrastLevel] = useState(50);

  // Audit state
  const [auditLogs, setAuditLogs] = useState([
    { id: 1, date: '2024-01-20 14:30', action: 'Profile updated', details: 'Changed job title' },
    { id: 2, date: '2024-01-19 09:15', action: 'Security settings modified', details: 'Enabled MFA' },
    { id: 3, date: '2024-01-18 16:45', action: 'Password changed', details: 'Password updated successfully' },
    { id: 4, date: '2024-01-15 11:20', action: 'Login from new device', details: 'Chrome on Windows' }
  ]);

  // Terminology Preferences state
  const [terminologySettings, setTerminologySettings] = useState({
    methodology: 'scrum',
    customTerms: {
      'theme': 'Theme',
      'initiative': 'Initiative', 
      'feature': 'Feature',
      'epic': 'Epic',
      'story': 'Story',
      'sprint': 'Sprint',
      'backlog-item': 'Backlog Item',
      'acceptance-criteria': 'Acceptance Criteria',
      'roadmap': 'Roadmap'
    }
  });
  
  const [terminologyDialogOpen, setTerminologyDialogOpen] = useState(false);
  const [terminologyHasChanges, setTerminologyHasChanges] = useState(false);
  const [tempTerminologySettings, setTempTerminologySettings] = useState(terminologySettings);

  // Data Governance state (moved from Admin)
  const [governanceData, setGovernanceData] = useState({
    complianceScore: 87,
    dataQualityScore: 92,
    auditStatus: 'passing',
    lastAudit: '2024-01-20',
    activeViolations: 3,
    resolvedViolations: 15,
    dataAssets: 142,
    governedFields: 89
  });

  // Compliance violations data (moved from Admin)
  const [violations, setViolations] = useState([
    {
      id: 'v1',
      type: 'Duplicate Naming',
      severity: 'Medium',
      entity: 'Feature: "User Dashboard"',
      date: '2024-01-19',
      status: 'Resolved',
      description: 'Multiple features with identical names detected'
    },
    {
      id: 'v2',
      type: 'Reserved Word Usage',
      severity: 'High',
      entity: 'Project: "System"',
      date: '2024-01-18',
      status: 'Active',
      description: 'Using system reserved words in entity names'
    },
    {
      id: 'v3',
      type: 'Invalid Characters',
      severity: 'Low',
      entity: 'Feature: "API-2.0"',
      date: '2024-01-17',
      status: 'Resolved',
      description: 'Special characters in entity names violate naming standards'
    }
  ]);

  // Copy snackbar state for governance section
  const [copySnackbar, setCopySnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Methodology definitions with default terms
  const methodologies = [
    {
      id: 'scrum',
      name: 'Scrum',
      description: 'Standard Scrum terminology and practices',
      defaultTerms: {
        'theme': 'Theme',
        'initiative': 'Initiative',
        'feature': 'Feature', 
        'epic': 'Epic',
        'story': 'User Story',
        'sprint': 'Sprint',
        'backlog-item': 'Product Backlog Item',
        'acceptance-criteria': 'Acceptance Criteria',
        'roadmap': 'Product Roadmap'
      }
    },
    {
      id: 'jira',
      name: 'Jira/Atlassian',
      description: 'Jira-aligned terminology for seamless integration',
      defaultTerms: {
        'theme': 'Theme',
        'initiative': 'Initiative',
        'feature': 'Feature',
        'epic': 'Epic',
        'story': 'Story',
        'sprint': 'Sprint',
        'backlog-item': 'Issue',
        'acceptance-criteria': 'Acceptance Criteria',
        'roadmap': 'Roadmap'
      }
    },
    {
      id: 'safe',
      name: 'SAFe (Scaled Agile)',
      description: 'Scaled Agile Framework terminology',
      defaultTerms: {
        'theme': 'Strategic Theme',
        'initiative': 'Portfolio Epic',
        'feature': 'Capability',
        'epic': 'Solution Epic',
        'story': 'Feature',
        'sprint': 'Iteration',
        'backlog-item': 'Program Backlog Item',
        'acceptance-criteria': 'Acceptance Criteria',
        'roadmap': 'Solution Roadmap'
      }
    },
    {
      id: 'azure-devops',
      name: 'Azure DevOps',
      description: 'Microsoft Azure DevOps terminology',
      defaultTerms: {
        'theme': 'Theme',
        'initiative': 'Initiative',
        'feature': 'Feature',
        'epic': 'Epic',
        'story': 'User Story',
        'sprint': 'Sprint',
        'backlog-item': 'Work Item',
        'acceptance-criteria': 'Acceptance Criteria',
        'roadmap': 'Feature Timeline'
      }
    },
    {
      id: 'custom',
      name: 'Custom',
      description: 'Define your own terminology preferences',
      defaultTerms: {
        'theme': 'Theme',
        'initiative': 'Initiative',
        'feature': 'Feature',
        'epic': 'Epic', 
        'story': 'Story',
        'sprint': 'Sprint',
        'backlog-item': 'Backlog Item',
        'acceptance-criteria': 'Acceptance Criteria',
        'roadmap': 'Roadmap'
      }
    }
  ];

  // Term definitions for help tooltips
  const termDefinitions = {
    'theme': 'High-level strategic direction or business objective that groups related initiatives',
    'initiative': 'Large body of work that delivers significant business value and may span multiple teams',
    'feature': 'Specific functionality or capability that provides value to end users',
    'epic': 'Large user story that can be broken down into smaller stories for development',
    'story': 'Small, manageable piece of functionality from the user\'s perspective',
    'sprint': 'Time-boxed period during which development work is completed',
    'backlog-item': 'Item in the product backlog representing work to be done',
    'acceptance-criteria': 'Conditions that must be met for a story to be considered complete',
    'roadmap': 'High-level view of product direction and planned features over time'
  };

  // Resizable navigation state
  const [navWidth, setNavWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('profile-nav-width');
      return saved ? parseInt(saved, 10) : 280;
    }
    return 280;
  });
  const [isResizing, setIsResizing] = useState(false);

  const navigationItems = [
    { id: 'profile-information', label: 'Profile Information', icon: <PersonIcon /> },
    { id: 'login-security', label: 'Login & Security', icon: <SecurityIcon /> },
    { id: 'language', label: 'Language & Region', icon: <GlobeIcon /> },
    { id: 'theme', label: 'Theme', icon: <PaletteIcon /> },
    { id: 'terminology', label: 'Terminology Preferences', icon: <TranslateIcon /> },
    { id: 'notifications', label: 'Notifications', icon: <BellIcon /> },
    { id: 'team', label: 'Team/Organization', icon: <BuildingIcon /> },
    { id: 'subscription', label: 'Subscription/Plan', icon: <CreditCardIcon /> },
    { id: 'billing', label: 'Billing & Payments', icon: <ReceiptIcon /> },
    { id: 'privacy', label: 'Privacy & Data Settings', icon: <ShieldIcon /> },
    { id: 'data-governance', label: 'Data Governance Dashboard', icon: <SecurityIcon /> },
    { id: 'terminology-governance', label: 'Naming Guidelines & Governance', icon: <TranslateIcon /> },
    { id: 'audit', label: 'Audit & Activity Logs', icon: <HistoryIcon /> },
    { id: 'account-closure', label: 'Account Closure/Export', icon: <TrashIcon /> }
  ];

  // Save width to localStorage
  useEffect(() => {
    localStorage.setItem('profile-nav-width', navWidth.toString());
  }, [navWidth]);

  // Resize handling
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const newWidth = e.clientX - 80;
    if (newWidth >= 240 && newWidth <= 360) {
      setNavWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileSave = () => {
    setEditMode(false);
  };

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileData(prev => ({ ...prev, avatarUrl: e.target?.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpgradePlan = (planId: string) => {
    setCurrentPlan(planId);
  };

  const handleNotificationChange = (setting: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotifications(prev => ({
      ...prev,
      [setting]: event.target.checked
    }));
  };

  const handlePrivacyChange = (setting: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrivacySettings(prev => ({
      ...prev,
      [setting]: event.target.checked
    }));
  };

  const handleThemeChange = (event: React.MouseEvent<HTMLElement>, newTheme: string | null) => {
    if (newTheme !== null) {
      setThemeMode(newTheme);
    }
  };

  // Terminology handlers
  const handleMethodologyChange = (event: { target: { value: string } }) => {
    const selectedMethodology = methodologies.find(m => m.id === event.target.value);
    if (selectedMethodology) {
      const newSettings = {
        methodology: selectedMethodology.id,
        customTerms: { ...selectedMethodology.defaultTerms }
      };
      setTempTerminologySettings(newSettings);
      setTerminologyHasChanges(
        JSON.stringify(newSettings) !== JSON.stringify(terminologySettings)
      );
    }
  };

  const handleTermChange = (termKey: string, newValue: string) => {
    const newSettings = {
      ...tempTerminologySettings,
      customTerms: {
        ...tempTerminologySettings.customTerms,
        [termKey]: newValue
      }
    };
    setTempTerminologySettings(newSettings);
    setTerminologyHasChanges(
      JSON.stringify(newSettings) !== JSON.stringify(terminologySettings)
    );
  };

  const handleSaveTerminology = () => {
    setTerminologySettings(tempTerminologySettings);
    setTerminologyHasChanges(false);
    setTerminologyDialogOpen(false);
  };

  const handleCancelTerminology = () => {
    if (terminologyHasChanges) {
      setTerminologyDialogOpen(true);
    } else {
      setTempTerminologySettings(terminologySettings);
    }
  };

  const handleDiscardChanges = () => {
    setTempTerminologySettings(terminologySettings);
    setTerminologyHasChanges(false);
    setTerminologyDialogOpen(false);
  };

  const getSelectedMethodology = () => {
    return methodologies.find(m => m.id === tempTerminologySettings.methodology);
  };

  // Data Governance handlers
  const handleRefreshGovernance = () => {
    // Simulate refresh of governance data
    setGovernanceData(prev => ({
      ...prev,
      complianceScore: Math.floor(Math.random() * 20) + 80,
      dataQualityScore: Math.floor(Math.random() * 20) + 80,
      lastAudit: new Date().toISOString().split('T')[0]
    }));
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'info';
      default: return 'default';
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return 'error';
    if (passwordStrength < 70) return 'warning';
    return 'success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 70) return 'Medium';
    return 'Strong';
  };

  const handleLogout = () => {
    // Here you would implement actual logout logic
    // For now, just redirect to projects view as a demo
    console.log('Logout clicked');
    onViewChange('projects');
  };

  const renderLogoutButton = () => (
    <MuiBox sx={{ p: 1 }}>
      <MuiButton
        fullWidth
        variant="text"
        startIcon={<LogoutIcon />}
        onClick={handleLogout}
        sx={{
          height: 48,
          justifyContent: 'flex-start',
          px: 2,
          borderRadius: 1,
          color: 'text.secondary',
          fontSize: '0.875rem',
          fontWeight: 400,
          textTransform: 'none',
          '&:hover': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '& .MuiSvgIcon-root': {
              color: 'primary.contrastText'
            }
          },
          '& .MuiSvgIcon-root': {
            fontSize: 20,
            color: 'text.secondary'
          }
        }}
      >
        Logout
      </MuiButton>
    </MuiBox>
  );

  const renderLocalNavigationDrawer = () => {
    // Convert navigationItems to include dividerAfter property
    // Logical groupings: Personal (0-4), System (5), Team/Business (6-8), Privacy/Admin/Governance (9-13) 
    const navigationItemsWithDividers = navigationItems.map((item, index) => ({
      ...item,
      dividerAfter: index === 4 || index === 5 || index === 8 || index === 11
    }));

    return (
      <ColumnNavigation
        items={navigationItemsWithDividers}
        selectedId={selectedSection}
        onItemSelect={setSelectedSection}
        resizable={!isMobile}
        initialWidth={280}
        storageKey="profile-nav-width"
        footer={renderLogoutButton()}
        sx={{
          width: navWidth,
          height: '100%',
          position: 'sticky',
          top: 0
        }}
      />
    );
  };

  // Profile Information Section
  const renderProfileInformation = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <PersonIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Profile Information
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Manage your personal information and avatar
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiCard elevation={1}>
          <MuiCardHeader
            title="Personal Information"
            subheader="Update your profile details and professional information"
            action={
              <MuiIconButton onClick={editMode ? handleProfileSave : () => setEditMode(true)}>
                {editMode ? <SaveIcon /> : <EditIcon />}
              </MuiIconButton>
            }
          />
          <MuiCardContent>
            <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Avatar Upload Section */}
              <MuiBox sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <MuiAvatar
                  src={profileData.avatarUrl}
                  sx={{ 
                    width: 72, 
                    height: 72,
                    cursor: editMode ? 'pointer' : 'default'
                  }}
                  onClick={editMode ? () => document.getElementById('avatar-upload')?.click() : undefined}
                >
                  {profileData.firstName.charAt(0)}{profileData.lastName.charAt(0)}
                </MuiAvatar>
                {editMode && (
                  <>
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleAvatarUpload}
                    />
                    <MuiButton
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => document.getElementById('avatar-upload')?.click()}
                      size="small"
                    >
                      Upload Photo
                    </MuiButton>
                  </>
                )}
              </MuiBox>

              <MuiDivider />

              {/* Profile Fields */}
              <MuiGrid container spacing={3}>
                {/* First Name and Last Name - Side by Side */}
                <MuiGrid item xs={12} md={6}>
                  <MuiTextField
                    fullWidth
                    label="First Name"
                    value={profileData.firstName}
                    disabled={!editMode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                    variant="outlined"
                  />
                </MuiGrid>
                <MuiGrid item xs={12} md={6}>
                  <MuiTextField
                    fullWidth
                    label="Last Name"
                    value={profileData.lastName}
                    disabled={!editMode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                    variant="outlined"
                  />
                </MuiGrid>
                
                {/* Email - Full Width, Non-editable */}
                <MuiGrid item xs={12}>
                  <MuiTextField
                    fullWidth
                    label="Email Address"
                    value={profileData.email}
                    disabled
                    variant="outlined"
                    InputProps={{
                      endAdornment: <LockIcon sx={{ color: 'text.disabled' }} />
                    }}
                    helperText="Email cannot be changed - contact admin"
                  />
                </MuiGrid>

                {/* Company - Full Width */}
                <MuiGrid item xs={12}>
                  <MuiTextField
                    fullWidth
                    label="Company"
                    value={profileData.company}
                    disabled={!editMode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, company: e.target.value }))}
                    variant="outlined"
                  />
                </MuiGrid>

                {/* Job Title - Full Width */}
                <MuiGrid item xs={12}>
                  <MuiTextField
                    fullWidth
                    label="Job Title"
                    value={profileData.jobTitle}
                    disabled={!editMode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, jobTitle: e.target.value }))}
                    variant="outlined"
                  />
                </MuiGrid>

                {/* Role - Full Width */}
                <MuiGrid item xs={12}>
                  <MuiTextField
                    fullWidth
                    label="Role"
                    value={profileData.role}
                    disabled={!editMode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, role: e.target.value }))}
                    variant="outlined"
                  />
                </MuiGrid>

                {/* Department - Full Width */}
                <MuiGrid item xs={12}>
                  <MuiTextField
                    fullWidth
                    label="Department"
                    value={profileData.department}
                    disabled={!editMode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, department: e.target.value }))}
                    variant="outlined"
                  />
                </MuiGrid>

                {/* Industry - Full Width */}
                <MuiGrid item xs={12}>
                  <MuiTextField
                    fullWidth
                    label="Industry"
                    value={profileData.industry}
                    disabled={!editMode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, industry: e.target.value }))}
                    variant="outlined"
                  />
                </MuiGrid>
              </MuiGrid>
            </MuiBox>
          </MuiCardContent>
        </MuiCard>
      </MuiFade>
    </MuiBox>
  );



  // Theme Section
  const renderTheme = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <PaletteIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Theme
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Customize your interface appearance and accessibility settings
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiCard elevation={1}>
          <MuiCardHeader
            title="Theme Preferences"
            subheader="Customize your interface appearance"
          />
          <MuiCardContent>
            <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Theme Toggle */}
              <MuiBox>
                <MuiTypography variant="subtitle1" sx={{ mb: 2 }}>
                  Theme Mode
                </MuiTypography>
                <MuiToggleButtonGroup
                  value={themeMode}
                  exclusive
                  onChange={handleThemeChange}
                  aria-label="theme mode"
                  fullWidth
                >
                  <MuiToggleButton value="light" aria-label="light mode">
                    <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SunIcon />
                      Light
                    </MuiBox>
                  </MuiToggleButton>
                  <MuiToggleButton value="dark" aria-label="dark mode">
                    <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MoonIcon />
                      Dark
                    </MuiBox>
                  </MuiToggleButton>
                  <MuiToggleButton value="system" aria-label="system mode">
                    <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MonitorIcon />
                      System
                    </MuiBox>
                  </MuiToggleButton>
                </MuiToggleButtonGroup>
              </MuiBox>

              <MuiDivider />

              {/* Contrast Slider */}
              <MuiBox>
                <MuiTypography variant="subtitle1" sx={{ mb: 2 }}>
                  Contrast Level
                </MuiTypography>
                <MuiSlider
                  value={contrastLevel}
                  onChange={(_, newValue) => setContrastLevel(newValue as number)}
                  aria-label="Contrast level"
                  valueLabelDisplay="auto"
                  step={10}
                  marks
                  min={0}
                  max={100}
                  sx={{ mt: 2 }}
                />
                <MuiTypography variant="caption" color="text.secondary">
                  Adjust interface contrast for better accessibility
                </MuiTypography>
              </MuiBox>
            </MuiBox>
          </MuiCardContent>
        </MuiCard>
      </MuiFade>
    </MuiBox>
  );

  // Terminology Preferences Section
  const renderTerminologyPreferences = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <TranslateIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Terminology Preferences
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Customize the vocabulary used throughout your workspace
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiCard elevation={1}>
          <MuiCardHeader
            title="SDLC Terminology"
            subheader="Customize the vocabulary used throughout your workspace to match your preferred methodology"
          />
          <MuiCardContent>
            <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Step 1: Methodology Selection */}
              <MuiBox>
                <MuiFormControl fullWidth variant="outlined">
                  <MuiInputLabel id="methodology-select-label">Preferred Methodology</MuiInputLabel>
                  <MuiSelect
                    labelId="methodology-select-label"
                    value={tempTerminologySettings.methodology}
                    onChange={handleMethodologyChange}
                    label="Preferred Methodology"
                    sx={{ mb: 2 }}
                  >
                    {methodologies.map((methodology) => (
                      <MuiMenuItem key={methodology.id} value={methodology.id}>
                        <MuiBox>
                          <MuiTypography variant="body1" sx={{ fontWeight: 500 }}>
                            {methodology.name}
                          </MuiTypography>
                          <MuiTypography variant="caption" color="text.secondary">
                            {methodology.description}
                          </MuiTypography>
                        </MuiBox>
                      </MuiMenuItem>
                    ))}
                  </MuiSelect>
                </MuiFormControl>
                
                {getSelectedMethodology() && (
                  <MuiBox 
                    sx={{ 
                      p: 2,
                      bgcolor: 'primary.light',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'primary.main',
                      opacity: 0.1
                    }}
                  >
                    <MuiTypography variant="body2" color="primary.dark">
                      {getSelectedMethodology()?.description}
                    </MuiTypography>
                  </MuiBox>
                )}
              </MuiBox>

              <MuiDivider />

              {/* Action Buttons */}
              <MuiBox sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <MuiButton 
                  variant="outlined" 
                  onClick={handleCancelTerminology}
                  disabled={!terminologyHasChanges}
                >
                  Cancel
                </MuiButton>
                <MuiButton 
                  variant="contained" 
                  onClick={handleSaveTerminology}
                  disabled={!terminologyHasChanges}
                  startIcon={<SaveIcon />}
                >
                  Save Changes
                </MuiButton>
              </MuiBox>
            </MuiBox>
          </MuiCardContent>
        </MuiCard>
      </MuiFade>
    </MuiBox>
  );

  // Login & Security Section
  const renderLoginSecurity = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <SecurityIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Login & Security
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Manage your password and security settings
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <MuiCard elevation={1}>
            <MuiCardHeader
              title="Password Security"
              subheader="Manage your password and authentication settings"
            />
            <MuiCardContent>
              <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <MuiBox>
                  <MuiBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <MuiTypography variant="subtitle1">Password Strength</MuiTypography>
                    <MuiChip
                      label={getPasswordStrengthText()}
                      color={getPasswordStrengthColor()}
                      size="small"
                    />
                  </MuiBox>
                  <MuiLinearProgress
                    variant="determinate"
                    value={passwordStrength}
                    color={getPasswordStrengthColor()}
                    sx={{ height: 8, borderRadius: 4, mb: 2 }}
                  />
                  <MuiTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Last changed: 30 days ago
                  </MuiTypography>
                  <MuiButton variant="outlined" startIcon={<LockIcon />}>
                    Change Password
                  </MuiButton>
                </MuiBox>

                <MuiDivider />

                <MuiBox>
                  <MuiBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <MuiBox>
                      <MuiTypography variant="subtitle1">Multi-Factor Authentication</MuiTypography>
                      <MuiTypography variant="body2" color="text.secondary">
                        Add an extra layer of security to your account
                      </MuiTypography>
                    </MuiBox>
                    <MuiChip
                      label={mfaEnabled ? 'Enabled' : 'Disabled'}
                      color={mfaEnabled ? 'success' : 'default'}
                      onClick={() => setShowMfaModal(true)}
                      clickable
                      icon={mfaEnabled ? <CheckIcon /> : <CloseIcon />}
                    />
                  </MuiBox>
                </MuiBox>
              </MuiBox>
            </MuiCardContent>
          </MuiCard>

          <MuiCard elevation={1}>
            <MuiCardHeader
              title="Recent Security Activity"
              subheader="Monitor recent changes and login events"
            />
            <MuiCardContent>
              <MuiTableContainer>
                <MuiTable size="small">
                  <MuiTableHead>
                    <MuiTableRow>
                      <MuiTableCell>Date</MuiTableCell>
                      <MuiTableCell>Event</MuiTableCell>
                      <MuiTableCell>IP Address</MuiTableCell>
                    </MuiTableRow>
                  </MuiTableHead>
                  <MuiTableBody>
                    {securityEvents.map((event, index) => (
                      <MuiTableRow key={index} hover>
                        <MuiTableCell>{event.date}</MuiTableCell>
                        <MuiTableCell>{event.event}</MuiTableCell>
                        <MuiTableCell>{event.ip}</MuiTableCell>
                      </MuiTableRow>
                    ))}
                  </MuiTableBody>
                </MuiTable>
              </MuiTableContainer>
            </MuiCardContent>
          </MuiCard>
        </MuiBox>
      </MuiFade>
    </MuiBox>
  );

  // Notifications Section
  const renderNotifications = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <BellIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Notifications
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Configure how you receive alerts and communications
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiCard elevation={1}>
          <MuiCardHeader
            title="Notification Preferences"
            subheader="Control how and when you receive notifications"
          />
          <MuiCardContent>
            <MuiGrid container spacing={2}>
              <MuiGrid item xs={12} sm={6}>
                <MuiFormControlLabel
                  control={
                    <MuiSwitch
                      checked={notifications.email}
                      onChange={handleNotificationChange('email')}
                    />
                  }
                  label="Email Notifications"
                />
                <MuiTypography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Receive notifications via email
                </MuiTypography>
              </MuiGrid>
              <MuiGrid item xs={12} sm={6}>
                <MuiFormControlLabel
                  control={
                    <MuiSwitch
                      checked={notifications.push}
                      onChange={handleNotificationChange('push')}
                    />
                  }
                  label="Push Notifications"
                />
                <MuiTypography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Browser push notifications
                </MuiTypography>
              </MuiGrid>
              <MuiGrid item xs={12} sm={6}>
                <MuiFormControlLabel
                  control={
                    <MuiSwitch
                      checked={notifications.sms}
                      onChange={handleNotificationChange('sms')}
                    />
                  }
                  label="SMS Notifications"
                />
                <MuiTypography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Text message alerts
                </MuiTypography>
              </MuiGrid>
              <MuiGrid item xs={12} sm={6}>
                <MuiFormControlLabel
                  control={
                    <MuiSwitch
                      checked={notifications.weeklyDigest}
                      onChange={handleNotificationChange('weeklyDigest')}
                    />
                  }
                  label="Weekly Digest"
                />
                <MuiTypography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Weekly summary emails
                </MuiTypography>
              </MuiGrid>
              <MuiGrid item xs={12} sm={6}>
                <MuiFormControlLabel
                  control={
                    <MuiSwitch
                      checked={notifications.projectUpdates}
                      onChange={handleNotificationChange('projectUpdates')}
                    />
                  }
                  label="Product Updates"
                />
                <MuiTypography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Updates on product progress
                </MuiTypography>
              </MuiGrid>
              <MuiGrid item xs={12} sm={6}>
                <MuiFormControlLabel
                  control={
                    <MuiSwitch
                      checked={notifications.teamMentions}
                      onChange={handleNotificationChange('teamMentions')}
                    />
                  }
                  label="Team Mentions"
                />
                <MuiTypography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  When mentioned by team members
                </MuiTypography>
              </MuiGrid>
            </MuiGrid>
          </MuiCardContent>
        </MuiCard>
      </MuiFade>
    </MuiBox>
  );

  // Subscription Section
  const renderSubscription = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <CreditCardIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Subscription/Plan
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Choose the plan that best fits your needs
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiCard elevation={1}>
          <MuiCardHeader
            title="Current Plan & Billing"
            subheader="Manage your subscription and billing preferences"
          />
          <MuiCardContent>
            <MuiTableContainer sx={{ mb: 3 }}>
              <MuiTable>
                <MuiTableHead>
                  <MuiTableRow>
                    <MuiTableCell>Plan</MuiTableCell>
                    <MuiTableCell>Price</MuiTableCell>
                    <MuiTableCell>Features</MuiTableCell>
                    <MuiTableCell align="center">Action</MuiTableCell>
                  </MuiTableRow>
                </MuiTableHead>
                <MuiTableBody>
                  {plans.map((plan) => (
                    <MuiTableRow
                      key={plan.id}
                      hover
                      sx={{
                        bgcolor: plan.current ? 'primary.light' : 'inherit',
                        opacity: plan.current ? 0.1 : 1,
                        '&:hover': {
                          bgcolor: plan.current ? 'primary.light' : 'action.hover',
                          opacity: plan.current ? 0.15 : 1
                        }
                      }}
                    >
                      <MuiTableCell>
                        <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MuiTypography variant="subtitle1" sx={{ fontWeight: plan.current ? 600 : 400 }}>
                            {plan.name}
                          </MuiTypography>
                          {plan.popular && (
                            <MuiChip label="Popular" color="primary" size="small" icon={<StarIcon />} />
                          )}
                          {plan.current && (
                            <MuiChip label="Current" color="success" size="small" />
                          )}
                        </MuiBox>
                      </MuiTableCell>
                      <MuiTableCell>
                        <MuiTypography variant="h6" color="primary">
                          {plan.price}
                        </MuiTypography>
                      </MuiTableCell>
                      <MuiTableCell>
                        <MuiBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {plan.features.map((feature, index) => (
                            <MuiChip
                              key={index}
                              label={feature}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </MuiBox>
                      </MuiTableCell>
                      <MuiTableCell align="center">
                        {plan.current ? (
                          <MuiChip label="Active" color="success" />
                        ) : (
                          <MuiButton
                            variant="contained"
                            color="primary"
                            onClick={() => handleUpgradePlan(plan.id)}
                            size="small"
                          >
                            Upgrade
                          </MuiButton>
                        )}
                      </MuiTableCell>
                    </MuiTableRow>
                  ))}
                </MuiTableBody>
              </MuiTable>
            </MuiTableContainer>
          </MuiCardContent>
        </MuiCard>
      </MuiFade>
    </MuiBox>
  );

  // Billing Section
  const renderBilling = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <ReceiptIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Billing & Payments
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Manage your billing information and payment history
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <MuiCard elevation={1}>
            <MuiCardHeader
              title="Payment Method"
              subheader="Manage your billing information"
            />
            <MuiCardContent>
              <MuiTypography variant="body1" sx={{ mb: 2 }}>
                Payment Method: **** **** **** 1234 (Visa)
              </MuiTypography>
              <MuiBox sx={{ display: 'flex', gap: 2 }}>
                <MuiButton variant="outlined">Update Payment Method</MuiButton>
                <MuiButton variant="outlined">Add Backup Payment</MuiButton>
              </MuiBox>
            </MuiCardContent>
          </MuiCard>

          <MuiCard elevation={1}>
            <MuiCardHeader
              title="Billing History"
              subheader="View your past invoices and payments"
              action={
                <MuiButton variant="outlined" startIcon={<DownloadIcon />}>
                  Download All
                </MuiButton>
              }
            />
            <MuiCardContent>
              <MuiTableContainer>
                <MuiTable>
                  <MuiTableHead>
                    <MuiTableRow>
                      <MuiTableCell>Date</MuiTableCell>
                      <MuiTableCell>Amount</MuiTableCell>
                      <MuiTableCell>Status</MuiTableCell>
                      <MuiTableCell>Invoice</MuiTableCell>
                      <MuiTableCell>Action</MuiTableCell>
                    </MuiTableRow>
                  </MuiTableHead>
                  <MuiTableBody>
                    {billingHistory.map((record) => (
                      <MuiTableRow key={record.id} hover>
                        <MuiTableCell>{record.date}</MuiTableCell>
                        <MuiTableCell>{record.amount}</MuiTableCell>
                        <MuiTableCell>
                          <MuiChip 
                            label={record.status} 
                            color={record.status === 'Paid' ? 'success' : 'default'}
                            size="small"
                          />
                        </MuiTableCell>
                        <MuiTableCell>{record.invoice}</MuiTableCell>
                        <MuiTableCell>
                          <MuiButton variant="outlined" size="small" startIcon={<DownloadIcon />}>
                            Download
                          </MuiButton>
                        </MuiTableCell>
                      </MuiTableRow>
                    ))}
                  </MuiTableBody>
                </MuiTable>
              </MuiTableContainer>
            </MuiCardContent>
          </MuiCard>
        </MuiBox>
      </MuiFade>
    </MuiBox>
  );

  // Privacy & Data Settings Section
  const renderPrivacySettings = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <PolicyIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Privacy & Data Settings
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Control how your data is used and shared
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiCard elevation={1}>
          <MuiCardHeader
            title="Data Privacy Controls"
            subheader="Manage your privacy preferences and data sharing settings"
          />
          <MuiCardContent>
            <MuiGrid container spacing={2}>
              <MuiGrid item xs={12}>
                <MuiFormControlLabel
                  control={
                    <MuiSwitch
                      checked={privacySettings.analytics}
                      onChange={handlePrivacyChange('analytics')}
                    />
                  }
                  label="Analytics & Performance Data"
                />
                <MuiTypography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Help improve our service by sharing anonymous usage data
                </MuiTypography>
              </MuiGrid>
              <MuiGrid item xs={12}>
                <MuiFormControlLabel
                  control={
                    <MuiSwitch
                      checked={privacySettings.marketing}
                      onChange={handlePrivacyChange('marketing')}
                    />
                  }
                  label="Marketing Communications"
                />
                <MuiTypography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Receive marketing emails and promotional content
                </MuiTypography>
              </MuiGrid>
              <MuiGrid item xs={12}>
                <MuiFormControlLabel
                  control={
                    <MuiSwitch
                      checked={privacySettings.thirdPartySharing}
                      onChange={handlePrivacyChange('thirdPartySharing')}
                    />
                  }
                  label="Third-Party Data Sharing"
                />
                <MuiTypography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Allow sharing of anonymized data with trusted partners
                </MuiTypography>
              </MuiGrid>
              <MuiGrid item xs={12}>
                <MuiFormControl fullWidth>
                  <MuiInputLabel>Profile Visibility</MuiInputLabel>
                  <MuiSelect
                    value={privacySettings.profileVisibility}
                    label="Profile Visibility"
                    onChange={(e) => setPrivacySettings(prev => ({ 
                      ...prev, 
                      profileVisibility: e.target.value 
                    }))}
                  >
                    <MuiMenuItem value="public">Public</MuiMenuItem>
                    <MuiMenuItem value="team">Team Only</MuiMenuItem>
                    <MuiMenuItem value="private">Private</MuiMenuItem>
                  </MuiSelect>
                </MuiFormControl>
              </MuiGrid>
            </MuiGrid>
          </MuiCardContent>
        </MuiCard>
      </MuiFade>
    </MuiBox>
  );

  // Language & Region Section
  const renderLanguageRegion = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <GlobeIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Language & Region
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Configure your localization preferences
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiCard elevation={1}>
          <MuiCardHeader
            title="Localization Settings"
            subheader="Customize language, timezone, and regional formats"
          />
          <MuiCardContent>
            <MuiGrid container spacing={3}>
              <MuiGrid item xs={12} md={6}>
                <MuiFormControl fullWidth>
                  <MuiInputLabel>Language</MuiInputLabel>
                  <MuiSelect
                    value={localeSettings.language}
                    label="Language"
                    onChange={(e) => setLocaleSettings(prev => ({ 
                      ...prev, 
                      language: e.target.value 
                    }))}
                  >
                    <MuiMenuItem value="en-US">English (US)</MuiMenuItem>
                    <MuiMenuItem value="en-GB">English (UK)</MuiMenuItem>
                    <MuiMenuItem value="es-ES">Spanish</MuiMenuItem>
                    <MuiMenuItem value="fr-FR">French</MuiMenuItem>
                    <MuiMenuItem value="de-DE">German</MuiMenuItem>
                  </MuiSelect>
                </MuiFormControl>
              </MuiGrid>
              <MuiGrid item xs={12} md={6}>
                <MuiFormControl fullWidth>
                  <MuiInputLabel>Timezone</MuiInputLabel>
                  <MuiSelect
                    value={localeSettings.timezone}
                    label="Timezone"
                    onChange={(e) => setLocaleSettings(prev => ({ 
                      ...prev, 
                      timezone: e.target.value 
                    }))}
                  >
                    <MuiMenuItem value="America/Los_Angeles">Pacific Time (PT)</MuiMenuItem>
                    <MuiMenuItem value="America/New_York">Eastern Time (ET)</MuiMenuItem>
                    <MuiMenuItem value="Europe/London">Greenwich Mean Time (GMT)</MuiMenuItem>
                    <MuiMenuItem value="Europe/Paris">Central European Time (CET)</MuiMenuItem>
                  </MuiSelect>
                </MuiFormControl>
              </MuiGrid>
              <MuiGrid item xs={12} md={6}>
                <MuiFormControl fullWidth>
                  <MuiInputLabel>Date Format</MuiInputLabel>
                  <MuiSelect
                    value={localeSettings.dateFormat}
                    label="Date Format"
                    onChange={(e) => setLocaleSettings(prev => ({ 
                      ...prev, 
                      dateFormat: e.target.value 
                    }))}
                  >
                    <MuiMenuItem value="MM/DD/YYYY">MM/DD/YYYY</MuiMenuItem>
                    <MuiMenuItem value="DD/MM/YYYY">DD/MM/YYYY</MuiMenuItem>
                    <MuiMenuItem value="YYYY-MM-DD">YYYY-MM-DD</MuiMenuItem>
                  </MuiSelect>
                </MuiFormControl>
              </MuiGrid>
              <MuiGrid item xs={12} md={6}>
                <MuiFormControl fullWidth>
                  <MuiInputLabel>Time Format</MuiInputLabel>
                  <MuiSelect
                    value={localeSettings.timeFormat}
                    label="Time Format"
                    onChange={(e) => setLocaleSettings(prev => ({ 
                      ...prev, 
                      timeFormat: e.target.value 
                    }))}
                  >
                    <MuiMenuItem value="12-hour">12-hour (AM/PM)</MuiMenuItem>
                    <MuiMenuItem value="24-hour">24-hour</MuiMenuItem>
                  </MuiSelect>
                </MuiFormControl>
              </MuiGrid>
            </MuiGrid>
          </MuiCardContent>
        </MuiCard>
      </MuiFade>
    </MuiBox>
  );

  // Team/Organization Section
  const renderTeamOrganization = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <GroupIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Team/Organization
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Your role and position within the organization
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiCard elevation={1}>
          <MuiCardHeader
            title="Organization Details"
            subheader="View and manage your organizational information"
          />
          <MuiCardContent>
            <MuiGrid container spacing={3}>
              <MuiGrid item xs={12} md={6}>
                <MuiTextField
                  fullWidth
                  label="Organization Name"
                  value={teamSettings.organizationName}
                  disabled
                  variant="outlined"
                  InputProps={{
                    endAdornment: <LockIcon sx={{ color: 'text.disabled' }} />
                  }}
                />
              </MuiGrid>
              <MuiGrid item xs={12} md={6}>
                <MuiTextField
                  fullWidth
                  label="Team Role"
                  value={teamSettings.teamRole}
                  variant="outlined"
                  onChange={(e) => setTeamSettings(prev => ({ 
                    ...prev, 
                    teamRole: e.target.value 
                  }))}
                />
              </MuiGrid>
              <MuiGrid item xs={12} md={6}>
                <MuiTextField
                  fullWidth
                  label="Department"
                  value={teamSettings.department}
                  disabled
                  variant="outlined"
                  InputProps={{
                    endAdornment: <LockIcon sx={{ color: 'text.disabled' }} />
                  }}
                />
              </MuiGrid>
              <MuiGrid item xs={12} md={6}>
                <MuiTextField
                  fullWidth
                  label="Manager"
                  value={teamSettings.manager}
                  disabled
                  variant="outlined"
                  InputProps={{
                    endAdornment: <LockIcon sx={{ color: 'text.disabled' }} />
                  }}
                />
              </MuiGrid>
              <MuiGrid item xs={12} md={6}>
                <MuiTextField
                  fullWidth
                  label="Direct Reports"
                  value={teamSettings.directReports}
                  type="number"
                  variant="outlined"
                  onChange={(e) => setTeamSettings(prev => ({ 
                    ...prev, 
                    directReports: parseInt(e.target.value) || 0
                  }))}
                />
              </MuiGrid>
            </MuiGrid>
          </MuiCardContent>
        </MuiCard>
      </MuiFade>
    </MuiBox>
  );

  // Audit & Activity Logs Section
  const renderAuditLogs = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <HistoryIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Audit & Activity Logs
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Track your account activities and security events
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiCard elevation={1}>
          <MuiCardHeader
            title="Account Activity"
            subheader="Monitor recent account changes and security events"
          />
          <MuiCardContent>
            <MuiTableContainer>
              <MuiTable>
                <MuiTableHead>
                  <MuiTableRow>
                    <MuiTableCell>Date & Time</MuiTableCell>
                    <MuiTableCell>Action</MuiTableCell>
                    <MuiTableCell>Details</MuiTableCell>
                  </MuiTableRow>
                </MuiTableHead>
                <MuiTableBody>
                  {auditLogs.map((log) => (
                    <MuiTableRow key={log.id} hover>
                      <MuiTableCell>{log.date}</MuiTableCell>
                      <MuiTableCell>{log.action}</MuiTableCell>
                      <MuiTableCell>{log.details}</MuiTableCell>
                    </MuiTableRow>
                  ))}
                </MuiTableBody>
              </MuiTable>
            </MuiTableContainer>
          </MuiCardContent>
        </MuiCard>
      </MuiFade>
    </MuiBox>
  );

  // Account Closure/Export Section
  const renderAccountClosure = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <AccountBalanceIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Account Closure/Export
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Export your data or permanently close your account
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <MuiCard elevation={1}>
            <MuiCardHeader
              title="Data Export"
              subheader="Download your personal data and account information"
            />
            <MuiCardContent>
              <MuiTypography variant="body1" sx={{ mb: 2 }}>
                You can export all your personal data including profile information, products, and activity logs.
              </MuiTypography>
              <MuiButton variant="outlined" startIcon={<DownloadIcon />}>
                Export Data
              </MuiButton>
            </MuiCardContent>
          </MuiCard>

          <MuiCard elevation={1} sx={{ borderColor: 'error.main', borderWidth: 1 }}>
            <MuiCardHeader
              title="Account Closure"
              subheader="Permanently delete your account and all associated data"
              titleTypographyProps={{ color: 'error.main' }}
            />
            <MuiCardContent>
              <MuiTypography variant="body1" sx={{ mb: 2 }}>
                This action cannot be undone. All your data, products, and team collaborations will be permanently deleted.
              </MuiTypography>
              <MuiButton variant="contained" color="error" startIcon={<TrashIcon />}>
                Close Account
              </MuiButton>
            </MuiCardContent>
          </MuiCard>
        </MuiBox>
      </MuiFade>
    </MuiBox>
  );

  // Data Governance Dashboard Section (moved from Admin)
  const renderDataGovernanceSection = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <SecurityIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox sx={{ flex: 1 }}>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Data Governance Dashboard
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Monitor data quality, compliance, and governance metrics across the platform
          </MuiTypography>
        </MuiBox>
        <MuiIconButton onClick={handleRefreshGovernance} sx={{ color: 'primary.main' }}>
          <RefreshIcon />
        </MuiIconButton>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Overview Cards */}
          <MuiGrid container spacing={3}>
            <MuiGrid item xs={12} sm={6} md={3}>
              <MuiCard elevation={2}>
                <MuiCardContent sx={{ textAlign: 'center' }}>
                  <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <MuiTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    {governanceData.complianceScore}%
                  </MuiTypography>
                  <MuiTypography variant="body2" color="text.secondary">
                    Compliance Score
                  </MuiTypography>
                </MuiCardContent>
              </MuiCard>
            </MuiGrid>
            <MuiGrid item xs={12} sm={6} md={3}>
              <MuiCard elevation={2}>
                <MuiCardContent sx={{ textAlign: 'center' }}>
                  <CloudDoneIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <MuiTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    {governanceData.dataQualityScore}%
                  </MuiTypography>
                  <MuiTypography variant="body2" color="text.secondary">
                    Data Quality
                  </MuiTypography>
                </MuiCardContent>
              </MuiCard>
            </MuiGrid>
            <MuiGrid item xs={12} sm={6} md={3}>
              <MuiCard elevation={2}>
                <MuiCardContent sx={{ textAlign: 'center' }}>
                  <StorageIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                  <MuiTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    {governanceData.dataAssets}
                  </MuiTypography>
                  <MuiTypography variant="body2" color="text.secondary">
                    Data Assets
                  </MuiTypography>
                </MuiCardContent>
              </MuiCard>
            </MuiGrid>
            <MuiGrid item xs={12} sm={6} md={3}>
              <MuiCard elevation={2}>
                <MuiCardContent sx={{ textAlign: 'center' }}>
                  <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <MuiTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    {governanceData.governedFields}
                  </MuiTypography>
                  <MuiTypography variant="body2" color="text.secondary">
                    Governed Fields
                  </MuiTypography>
                </MuiCardContent>
              </MuiCard>
            </MuiGrid>
          </MuiGrid>

          {/* Audit Status */}
          <MuiCard elevation={1}>
            <MuiCardHeader
              title="Audit Status"
              subheader={`Last audit: ${governanceData.lastAudit}`}
            />
            <MuiCardContent>
              <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CheckCircleIcon sx={{ color: 'success.main' }} />
                <MuiTypography variant="body1">
                  Overall Status: 
                  <MuiChip 
                    label={governanceData.auditStatus.toUpperCase()} 
                    color="success" 
                    size="small" 
                    sx={{ ml: 1 }} 
                  />
                </MuiTypography>
              </MuiBox>
              
              <MuiBox sx={{ display: 'flex', gap: 4, mb: 2 }}>
                <MuiBox>
                  <MuiTypography variant="body2" color="text.secondary">
                    Active Violations
                  </MuiTypography>
                  <MuiTypography variant="h6" color="warning.main">
                    {governanceData.activeViolations}
                  </MuiTypography>
                </MuiBox>
                <MuiBox>
                  <MuiTypography variant="body2" color="text.secondary">
                    Resolved This Month
                  </MuiTypography>
                  <MuiTypography variant="h6" color="success.main">
                    {governanceData.resolvedViolations}
                  </MuiTypography>
                </MuiBox>
              </MuiBox>

              <MuiLinearProgress 
                variant="determinate" 
                value={governanceData.complianceScore} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </MuiCardContent>
          </MuiCard>

          {/* Compliance Violations Table */}
          <MuiCard elevation={1}>
            <MuiCardHeader
              title="Compliance Violations"
              subheader="Monitor violations and their resolution status"
            />
            <MuiCardContent>
              <MuiTableContainer component={MuiPaper} sx={{ maxHeight: 600, overflow: 'auto' }}>
                <MuiTable stickyHeader>
                  <MuiTableHead>
                    <MuiTableRow>
                      <MuiTableCell sx={{ minWidth: 140 }}>Violation Type</MuiTableCell>
                      <MuiTableCell sx={{ minWidth: 80 }}>Severity</MuiTableCell>
                      <MuiTableCell sx={{ minWidth: 140 }}>Entity</MuiTableCell>
                      <MuiTableCell sx={{ minWidth: 90 }}>Date</MuiTableCell>
                      <MuiTableCell sx={{ minWidth: 80 }}>Status</MuiTableCell>
                    </MuiTableRow>
                  </MuiTableHead>
                  <MuiTableBody>
                    {violations.map((violation) => (
                      <MuiTableRow key={violation.id}>
                        <MuiTableCell>
                          <MuiBox>
                            <MuiTypography variant="body2" sx={{ fontWeight: 500 }}>
                              {violation.type}
                            </MuiTypography>
                            <MuiTypography variant="caption" color="text.secondary">
                              {violation.description}
                            </MuiTypography>
                          </MuiBox>
                        </MuiTableCell>
                        <MuiTableCell>
                          <MuiChip 
                            label={violation.severity} 
                            color={getSeverityColor(violation.severity) as any} 
                            size="small" 
                          />
                        </MuiTableCell>
                        <MuiTableCell>{violation.entity}</MuiTableCell>
                        <MuiTableCell>{violation.date}</MuiTableCell>
                        <MuiTableCell>
                          <MuiChip 
                            label={violation.status} 
                            color={violation.status === 'Resolved' ? 'success' : 'warning'} 
                            size="small" 
                          />
                        </MuiTableCell>
                      </MuiTableRow>
                    ))}
                  </MuiTableBody>
                </MuiTable>
              </MuiTableContainer>
            </MuiCardContent>
          </MuiCard>
        </MuiBox>
      </MuiFade>
    </MuiBox>
  );

  // Terminology Governance Section (moved from Admin)
  const renderTerminologyGovernance = () => (
    <MuiBox>
      {/* Section.Header */}
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <TerminologyIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <MuiBox>
          <MuiTypography variant="h4" sx={{ fontWeight: 600 }}>
            Naming Guidelines & Governance
          </MuiTypography>
          <MuiTypography variant="body1" color="text.secondary">
            Manage platform-wide naming standards and terminology consistency
          </MuiTypography>
        </MuiBox>
      </MuiBox>

      {/* Section.Content */}
      <MuiFade in timeout={300}>
        <MuiCard elevation={1}>
          <MuiCardHeader
            title="Terminology Management"
            subheader="Configure platform-wide naming standards and methodology alignment"
          />
          <MuiCardContent>
            <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Methodology Selection */}
              <MuiBox>
                <MuiFormControl fullWidth variant="outlined">
                  <MuiInputLabel id="methodology-select-label">Platform Methodology</MuiInputLabel>
                  <MuiSelect
                    labelId="methodology-select-label"
                    value={tempTerminologySettings.methodology}
                    onChange={handleMethodologyChange}
                    label="Platform Methodology"
                    sx={{ mb: 2 }}
                  >
                    {methodologies.map((methodology) => (
                      <MuiMenuItem key={methodology.id} value={methodology.id}>
                        <MuiBox>
                          <MuiTypography variant="body1" sx={{ fontWeight: 500 }}>
                            {methodology.name}
                          </MuiTypography>
                          <MuiTypography variant="caption" color="text.secondary">
                            {methodology.description}
                          </MuiTypography>
                        </MuiBox>
                      </MuiMenuItem>
                    ))}
                  </MuiSelect>
                </MuiFormControl>
                
                {getSelectedMethodology() && (
                  <MuiBox 
                    sx={{ 
                      p: 2,
                      bgcolor: 'primary.light',
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'primary.main',
                      opacity: 0.1
                    }}
                  >
                    <MuiTypography variant="body2" color="primary.dark">
                      {getSelectedMethodology()?.description}
                    </MuiTypography>
                  </MuiBox>
                )}
              </MuiBox>

              <MuiDivider />

              {/* Terminology Table */}
              <MuiBox>
                <MuiTypography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Current Terminology Mapping
                </MuiTypography>
                <MuiTableContainer component={MuiPaper} variant="outlined">
                  <MuiTable size="small">
                    <MuiTableHead>
                      <MuiTableRow>
                        <MuiTableCell sx={{ fontWeight: 600 }}>Concept</MuiTableCell>
                        <MuiTableCell sx={{ fontWeight: 600 }}>Platform Term</MuiTableCell>
                        <MuiTableCell sx={{ fontWeight: 600 }}>Definition</MuiTableCell>
                      </MuiTableRow>
                    </MuiTableHead>
                    <MuiTableBody>
                      {Object.entries(tempTerminologySettings.customTerms).map(([key, value]) => (
                        <MuiTableRow key={key}>
                          <MuiTableCell sx={{ textTransform: 'capitalize' }}>
                            {key.replace('-', ' ')}
                          </MuiTableCell>
                          <MuiTableCell>
                            <MuiChip 
                              label={value as string} 
                              variant="outlined" 
                              size="small" 
                              color="primary"
                            />
                          </MuiTableCell>
                          <MuiTableCell>
                            <MuiTypography variant="caption" color="text.secondary">
                              {termDefinitions[key as keyof typeof termDefinitions]}
                            </MuiTypography>
                          </MuiTableCell>
                        </MuiTableRow>
                      ))}
                    </MuiTableBody>
                  </MuiTable>
                </MuiTableContainer>
              </MuiBox>

              {/* Action Buttons */}
              <MuiBox sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <MuiButton 
                  variant="outlined" 
                  onClick={handleCancelTerminology}
                  disabled={!terminologyHasChanges}
                >
                  Cancel
                </MuiButton>
                <MuiButton 
                  variant="contained" 
                  onClick={handleSaveTerminology}
                  disabled={!terminologyHasChanges}
                  startIcon={<SaveIcon />}
                >
                  Save Changes
                </MuiButton>
              </MuiBox>
            </MuiBox>
          </MuiCardContent>
        </MuiCard>
      </MuiFade>
    </MuiBox>
  );

  const renderContent = () => {
    switch (selectedSection) {
      case 'profile-information':
        return renderProfileInformation();
      case 'login-security':
        return renderLoginSecurity();
      case 'language':
        return renderLanguageRegion();
      case 'theme':
        return renderTheme();
      case 'terminology':
        return renderTerminologyPreferences();
      case 'notifications':
        return renderNotifications();
      case 'team':
        return renderTeamOrganization();
      case 'subscription':
        return renderSubscription();
      case 'billing':
        return renderBilling();
      case 'privacy':
        return renderPrivacySettings();
      case 'data-governance':
        return renderDataGovernanceSection();
      case 'terminology-governance':
        return renderTerminologyGovernance();
      case 'audit':
        return renderAuditLogs();
      case 'account-closure':
        return renderAccountClosure();
      default:
        return renderProfileInformation();
    }
  };

  return (
    <>
      {/* Fixed-Width Container - Unaffected by AI Assistant Panel */}
      <MuiBox
        sx={{
          position: 'fixed',
          top: 0,
          left: 80, // Start after navigation rail (80px)
          right: 0, // Extend to full width regardless of AI panel
          height: '100vh',
          minWidth: 1360, // 1440px - 80px navigation rail = 1360px minimum content width
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          zIndex: 1000, // Below AI Assistant panel (z-index 1300)
          overflow: 'hidden'
        }}
      >
      {/* Page.Header.Context - Profile Main Header */}
      <MuiPaper 
        component="header"
        elevation={0}
        sx={{ 
          height: 80, // Fixed height as per specs
          display: 'flex',
          alignItems: 'center', // Middle vertical alignment
          px: 3, // 24px padding left/right (MUI spacing-3)
          borderBottom: 1,
          borderColor: 'divider',
          borderRadius: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1100, // Above content but below modals
          bgcolor: '#F1F5FE' // Consistent Page.Header background
        }}
      >
        {/* Single line format: "Heading | Description" with 16px gap */}
        <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2 }}> {/* 16px gap (MUI spacing-2) */}
          {/* Profile Heading - H4 with 600 weight */}
          <MuiTypography 
            variant="h4" 
            sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '1.5rem',
              whiteSpace: 'nowrap' // Prevent wrapping
            }}
          >
            Profile
          </MuiTypography>
          
          {/* Vertical divider */}
          <MuiTypography 
            variant="h4"
            sx={{ 
              color: 'text.disabled',
              fontWeight: 300,
              fontSize: '1.5rem'
            }}
          >
            |
          </MuiTypography>
          
          {/* Description - Body1 with 400 weight and ellipsis overflow */}
          <MuiTypography 
            variant="body1"
            sx={{ 
              color: 'text.secondary',
              fontSize: '1rem',
              fontWeight: 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1 // Take remaining space for proper ellipsis
            }}
          >
            Manage your personal information, security settings, and preferences
          </MuiTypography>
        </MuiBox>
      </MuiPaper>

      {/* Body Container - MUI Surface with Fixed Layout */}
      <MuiBox 
        component="main"
        sx={{ 
          display: 'flex', 
          flex: 1, 
          overflow: 'hidden',
          position: 'relative' // Establish stacking context
        }}
      >
        {/* Mobile App Bar */}
        {isMobile && (
          <MuiPaper
            sx={{
              position: 'fixed',
              top: 80, // Below header
              left: 80, // After navigation rail
              right: 0,
              zIndex: theme.zIndex.appBar,
              p: 2, // MUI spacing-2
              display: 'flex',
              alignItems: 'center',
              gap: 2 // MUI spacing-2
            }}
          >
            <MuiIconButton onClick={handleDrawerToggle}>
              <MenuIcon />
            </MuiIconButton>
            <MuiTypography variant="h6">Profile & Account Settings</MuiTypography>
          </MuiPaper>
        )}

        {/* Column.Navigation.Collapsible - Local Profile Navigation Drawer - MUI Surface */}
        <MuiDrawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          sx={{
            width: navWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: navWidth,
              boxSizing: 'border-box',
              bgcolor: '#F1F5FE', // Consistent navigation background
              borderRight: 1,
              borderColor: 'divider',
              left: 0,
              zIndex: isMobile ? theme.zIndex.drawer : theme.zIndex.drawer - 1,
              transition: 'width 0.3s ease',
              position: 'sticky',
              height: 'calc(100vh - 80px)', // Adjust height to account for header
              overflowX: 'hidden'
            }
          }}
          elevation={1}
        >
          {renderLocalNavigationDrawer()}
        </MuiDrawer>

        {/* Main Content Area - MUI Container with Fixed Width */}
        <MuiContainer
          component="section"
          maxWidth={false}
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            height: 'calc(100vh - 80px)',
            overflow: 'auto',
            pt: isMobile ? 6 : 3, // MUI spacing-6 for mobile, spacing-3 for desktop
            px: 3, // MUI spacing-3
            pb: 3, // MUI spacing-3
            width: '100%',
            minWidth: 0 // Allow content to shrink if needed
          }}
        >
          {renderContent()}
        </MuiContainer>
      </MuiBox>

      {/* MFA Modal */}
      <MuiDialog open={showMfaModal} onClose={() => setShowMfaModal(false)} maxWidth="sm" fullWidth>
        <MuiDialogTitle>
          Multi-Factor Authentication
        </MuiDialogTitle>
        <MuiDialogContent>
          <MuiTypography variant="body1" sx={{ mb: 2 }}>
            {mfaEnabled 
              ? 'Are you sure you want to disable multi-factor authentication? This will reduce your account security.'
              : 'Enable multi-factor authentication to add an extra layer of security to your account.'
            }
          </MuiTypography>
          <MuiFormControlLabel
            control={
              <MuiSwitch
                checked={mfaEnabled}
                onChange={(e) => setMfaEnabled(e.target.checked)}
              />
            }
            label="Enable Multi-Factor Authentication"
          />
        </MuiDialogContent>
        <MuiDialogActions>
          <MuiButton onClick={() => setShowMfaModal(false)}>Cancel</MuiButton>
          <MuiButton 
            onClick={() => setShowMfaModal(false)} 
            variant="contained"
            color={mfaEnabled ? 'error' : 'primary'}
          >
            {mfaEnabled ? 'Disable MFA' : 'Enable MFA'}
          </MuiButton>
        </MuiDialogActions>
      </MuiDialog>

      {/* Terminology Dialog */}
      <MuiDialog
        open={terminologyDialogOpen}
        onClose={() => setTerminologyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <MuiDialogTitle>
          Discard Changes?
        </MuiDialogTitle>
        <MuiDialogContent>
          <MuiTypography variant="body1">
            You have unsaved changes to your terminology preferences. Are you sure you want to discard these changes?
          </MuiTypography>
        </MuiDialogContent>
        <MuiDialogActions sx={{ p: 3 }}>
          <MuiButton 
            onClick={() => setTerminologyDialogOpen(false)}
            variant="outlined"
          >
            Keep Editing
          </MuiButton>
          <MuiButton 
            onClick={handleDiscardChanges}
            variant="contained"
            color="error"
            startIcon={<CloseIcon />}
          >
            Discard Changes
          </MuiButton>
        </MuiDialogActions>
      </MuiDialog>

      {/* Copy Snackbar for Data Governance */}
      <MuiSnackbar
        open={copySnackbar.open}
        autoHideDuration={6000}
        onClose={() => setCopySnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert
          onClose={() => setCopySnackbar(prev => ({ ...prev, open: false }))}
          severity={copySnackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {copySnackbar.message}
        </MuiAlert>
      </MuiSnackbar>
      </MuiBox>
    </>
  );
};

export default Profile;